/**
 * useConfirmStore - Custom Confirm Dialog State
 *
 * Replaces native window.confirm() with a styled dialog.
 * Usage: `const ok = await confirmDialog({ title, message })`
 *
 * @module useConfirmStore
 */

import { create } from "zustand";

export interface ConfirmOptions {
  /** Dialog title */
  title: string;
  /** Dialog message (supports line breaks) */
  message?: string;
  /** Confirm button text (default: "확인") */
  confirmText?: string;
  /** Cancel button text (default: "취소") */
  cancelText?: string;
  /** Danger mode - red confirm button */
  danger?: boolean;
}

interface ConfirmState {
  isOpen: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: boolean) => void) | null;
  open: (options: ConfirmOptions) => Promise<boolean>;
  close: (result: boolean) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  isOpen: false,
  options: null,
  resolve: null,

  open: (options) => {
    return new Promise<boolean>((resolve) => {
      set({ isOpen: true, options, resolve });
    });
  },

  close: (result) => {
    const { resolve } = get();
    resolve?.(result);
    set({ isOpen: false, options: null, resolve: null });
  },
}));

/**
 * Show a custom confirm dialog. Drop-in replacement for window.confirm().
 *
 * @example
 * ```ts
 * const ok = await confirmDialog({
 *   title: '"권민서" 수업을 삭제하시겠습니까?',
 *   danger: true,
 * });
 * if (ok) { deleteSession(id); }
 * ```
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().open(options);
}
